import { ReactNode, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErroNaTela } from "./components/ErroNaTela";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Perfil from "./pages/Perfil";
// As telas da equipe são baixadas só por quem entra nelas. Num pacote só, o
// aluno baixava no celular o editor de cards, o importador de Word e as
// tabelas de administração para abrir a tela de estudo.
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsuarios = lazy(() => import("./pages/admin/Usuarios"));
const AdminCards = lazy(() => import("./pages/admin/Cards"));
const AdminCategorias = lazy(() => import("./pages/admin/Categorias"));
const AdminGrupos = lazy(() => import("./pages/admin/Grupos"));
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import AlunoDashboard from "./pages/aluno/Dashboard";
import AlunoCards from "./pages/aluno/Cards";
import { authAPI } from "./lib/api";
import { loginUnicoLigado } from "./lib/sessao-unica";
import MandaParaAPlataforma from "./pages/MandaParaAPlataforma";
import { isStudentPreview } from "./lib/studentPreview";

const queryClient = new QueryClient();

/*
  As três telas de senha, ou o caminho para a entrada única.

  Com login único não há senha deste produto para digitar, esquecer ou
  redefinir: quem autentica é a plataforma. As telas continuam no código porque
  `francesnativo.com/memoire` roda o mesmo pacote e ainda depende delas — o que
  decide é a variável de ambiente, não uma cópia diferente do front.

  Trocar a tela não fecha nada sozinho: o `auth.php` continuaria aceitando um
  POST de login vindo de fora do navegador. Quem fecha é o `LOGIN_PROPRIO` no
  servidor; isto aqui é o que a pessoa vê.
*/
const semSenhaPropria = (tela: ReactNode) =>
  loginUnicoLigado ? <MandaParaAPlataforma /> : tela;

// Proteger rotas exigindo autenticação e bloqueando se a senha precisa ser alterada
const RequireAuth = ({ children, requireRole }: { children: ReactNode; requireRole?: 'admin' | 'aluno' }) => {
  const user = authAPI.getCurrentUser();
  const isAuth = authAPI.isAuthenticated();

  if (!isAuth || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/alterar-senha" replace />;
  }

  if (requireRole === 'admin' && user.tipo_usuario !== 'admin' && user.tipo_usuario !== 'professor') {
    return <Navigate to="/aluno" replace />;
  }

  return <>{children}</>;
};

const RequireStudentAccess = ({ children }: { children: ReactNode }) => {
  const user = authAPI.getCurrentUser();

  if (!authAPI.isAuthenticated() || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/alterar-senha" replace />;
  }

  const canPreview = (user.tipo_usuario === "admin" || user.tipo_usuario === "professor") && isStudentPreview();
  if (user.tipo_usuario !== "aluno" && !canPreview) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

// Rota de alteração de senha
const ChangePasswordRoute = () => {
  const user = authAPI.getCurrentUser();
  const isAuth = authAPI.isAuthenticated();

  if (!isAuth || !user) {
    return <Navigate to="/login" replace />;
  }

  return <ChangePassword />;
};

/** Um erro numa tela não derruba as outras: trocar de página limpa o erro. */
const TelaProtegida = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <ErroNaTela resetKey={pathname}>{children}</ErroNaTela>;
};

const Carregando = () => (
  <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
    <span className="sr-only">Carregando…</span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <TelaProtegida>
        <Suspense fallback={<Carregando />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={semSenhaPropria(<Login />)} />
          <Route path="/forgot-password" element={semSenhaPropria(<ForgotPassword />)} />
          <Route path="/reset-password" element={semSenhaPropria(<ResetPassword />)} />
          <Route path="/alterar-senha" element={<ChangePasswordRoute />} />
          <Route path="/change-password" element={<ChangePasswordRoute />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<RequireAuth requireRole="admin"><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/usuarios" element={<RequireAuth requireRole="admin"><AdminUsuarios /></RequireAuth>} />
          <Route path="/admin/cards" element={<RequireAuth requireRole="admin"><AdminCards /></RequireAuth>} />
          <Route path="/admin/categorias" element={<RequireAuth requireRole="admin"><AdminCategorias /></RequireAuth>} />
          <Route path="/admin/grupos" element={<RequireAuth requireRole="admin"><AdminGrupos /></RequireAuth>} />

          {/* Aluno Routes */}
          <Route path="/aluno" element={<RequireStudentAccess><AlunoDashboard /></RequireStudentAccess>} />
          <Route path="/aluno/cards" element={<RequireStudentAccess><AlunoCards /></RequireStudentAccess>} />

          {/* Shared Routes */}
          <Route path="/perfil" element={<RequireAuth><Perfil /></RequireAuth>} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </TelaProtegida>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
